import type {
  Achievement,
  Challenge,
  Club,
  DailyItem,
  HappeningItem,
  ReputationFacet,
  SocialAuthor,
  SocialCommunity,
  SocialPost,
  Story,
  TrendingTopic,
} from '../types/social'

/**
 * Seeded demonstration content for the FUTA Akure pilot campus.
 * Marked as development data on the administrative surfaces — none of these
 * counts are production metrics.
 */

export const MY_CAMPUS = 'cmp-futa-main'

const authors: Record<string, SocialAuthor> = {
  ifeoma: {
    id: 'CAMP-4KD91LZP',
    kind: 'student',
    name: 'Ifeoma Eze',
    handle: 'ifeoma.e',
    initials: 'IE',
    context: '400L Mechanical Engineering',
    verified: true,
  },
  tunde: {
    id: 'CAMP-7QW32MRT',
    kind: 'student',
    name: 'Tunde Bakare',
    handle: 'tunde.b',
    initials: 'TB',
    context: '300L Computer Science',
    verified: true,
  },
  chidera: {
    id: 'CAMP-9ZX45NBV',
    kind: 'student',
    name: 'Chidera Okonkwo',
    handle: 'chidera.o',
    initials: 'CO',
    context: '200L Industrial Design',
    verified: false,
  },
  esa: {
    id: 'ORG-ESA-FUTA',
    kind: 'club',
    name: 'Engineering Students Association',
    handle: 'esa.futa',
    initials: 'ES',
    context: 'Verified club · School of Engineering',
    verified: true,
  },
  registry: {
    id: 'ORG-FUTA-REG',
    kind: 'institution',
    name: 'FUTA Academic Registry',
    handle: 'futa.registry',
    initials: 'FR',
    context: 'Official institutional account',
    verified: true,
  },
  seyi: {
    id: 'PRV-SEYI-01',
    kind: 'provider',
    name: 'Seyi Adewale',
    handle: 'seyi.repairs',
    initials: 'SA',
    context: 'Verified provider · Device repair',
    verified: true,
  },
  cedar: {
    id: 'PRP-CEDAR',
    kind: 'property',
    name: 'Cedarwood Residence',
    handle: 'cedarwood',
    initials: 'CR',
    context: 'Campuzon Verified property · Oba Ile',
    verified: true,
  },
}

export const socialAuthors = authors

export const posts: SocialPost[] = [
  {
    id: 'pst-1',
    kind: 'announcement',
    author: authors.registry,
    body: 'Course registration for the 2026/27 first semester closes on 28 August. Units added after that date require a written departmental waiver. Check your unit load in Academics before the deadline.',
    at: '2026-08-14T07:10:00Z',
    audience: 'institution',
    audienceLabel: 'Everyone at FUTA',
    spaceId: null,
    campusId: MY_CAMPUS,
    state: 'published',
    reactions: 214,
    myReaction: false,
    saved: true,
    comments: [],
    commentsLocked: true,
    poll: null,
    answers: [],
    resolved: false,
    attached: {
      kind: 'course',
      label: 'Your registration',
      detail: '18 of 24 units registered',
      to: '/academics',
    },
    imagePrompt: null,
    reason: 'Official notice from your institution. Official content is never ranked or hidden.',
    removalReason: null,
  },
  {
    id: 'pst-2',
    kind: 'event',
    author: authors.esa,
    body: 'Career talk this morning with three engineers from Lagos-based firms — CV clinic straight after. Bring a printed copy if you have one.',
    at: '2026-08-14T06:00:00Z',
    audience: 'campus',
    audienceLabel: 'FUTA Akure campus',
    spaceId: 'com-eng',
    campusId: MY_CAMPUS,
    state: 'published',
    reactions: 61,
    myReaction: true,
    saved: false,
    comments: [
      {
        id: 'cmt-1',
        author: authors.tunde,
        body: 'Is the CV clinic open to 300L or final year only?',
        at: '2026-08-14T06:30:00Z',
        parentId: null,
        mentions: [],
        removed: false,
      },
      {
        id: 'cmt-2',
        author: authors.esa,
        body: '@tunde.b open to everyone. Final year gets priority in the first hour.',
        at: '2026-08-14T06:41:00Z',
        parentId: 'cmt-1',
        mentions: ['tunde.b'],
        removed: false,
      },
    ],
    commentsLocked: false,
    poll: null,
    answers: [],
    resolved: false,
    attached: {
      kind: 'event',
      label: 'ESA Career Talk',
      detail: 'Today 10:00 · Engineering Auditorium',
      to: '/community',
    },
    imagePrompt: null,
    reason: 'From Engineering Students Association, a community you joined.',
    removalReason: null,
  },
  {
    id: 'pst-3',
    kind: 'question',
    author: authors.chidera,
    body: 'Where can I get a laptop screen replaced near campus without paying Lagos prices? Mine cracked yesterday.',
    at: '2026-08-13T18:20:00Z',
    audience: 'campus',
    audienceLabel: 'FUTA Akure campus',
    spaceId: null,
    campusId: MY_CAMPUS,
    state: 'published',
    reactions: 12,
    myReaction: false,
    saved: false,
    comments: [],
    commentsLocked: false,
    poll: null,
    answers: [
      {
        id: 'ans-1',
        author: authors.seyi,
        body: 'I do screen replacements — ₦42,000 to ₦68,000 depending on the panel. Bring the model number. Work is ordered through Campuzon so the payment is held until you confirm it works.',
        at: '2026-08-13T19:02:00Z',
        verified: true,
        aiGenerated: false,
        helpful: 18,
      },
      {
        id: 'ans-2',
        author: authors.tunde,
        body: 'There is a place at the Aule junction but I would not leave a laptop there overnight.',
        at: '2026-08-13T19:40:00Z',
        verified: false,
        aiGenerated: false,
        helpful: 4,
      },
    ],
    resolved: false,
    attached: {
      kind: 'service',
      label: 'Device repair on Campuzon',
      detail: '4 verified providers near Akure',
      to: '/marketplace',
    },
    imagePrompt: null,
    reason: 'A question from your campus that nobody has resolved yet.',
    removalReason: null,
  },
  {
    id: 'pst-4',
    kind: 'poll',
    author: authors.esa,
    body: 'Departmental social — where should we hold it? Voting closes Friday evening.',
    at: '2026-08-12T14:00:00Z',
    audience: 'community',
    audienceLabel: 'Engineering community members',
    spaceId: 'com-eng',
    campusId: MY_CAMPUS,
    state: 'published',
    reactions: 24,
    myReaction: false,
    saved: false,
    comments: [],
    commentsLocked: false,
    poll: {
      question: 'Where should the departmental social hold?',
      options: [
        { id: 'opt-1', label: 'Student Centre', votes: 84 },
        { id: 'opt-2', label: 'Faculty Hall', votes: 51 },
        { id: 'opt-3', label: 'Sports Complex', votes: 39 },
      ],
      closesAt: '2026-08-15T20:00:00Z',
      anonymous: true,
      myVote: null,
      eligibility: 'Engineering community members only. One vote per account.',
    },
    answers: [],
    resolved: false,
    attached: null,
    imagePrompt: null,
    reason: 'A poll you are eligible to vote in.',
    removalReason: null,
  },
  {
    id: 'pst-5',
    kind: 'gig',
    author: authors.ifeoma,
    body: 'Looking for a photographer for our departmental dinner on the 29th. Three hours, edited photos delivered within a week. Budget around ₦45,000.',
    at: '2026-08-12T09:15:00Z',
    audience: 'campus',
    audienceLabel: 'FUTA Akure campus',
    spaceId: null,
    campusId: MY_CAMPUS,
    state: 'published',
    reactions: 9,
    myReaction: false,
    saved: false,
    comments: [],
    commentsLocked: false,
    poll: null,
    answers: [],
    resolved: false,
    attached: {
      kind: 'service',
      label: 'Turn this into a service request',
      detail: 'Providers respond, and the order runs through escrow',
      to: '/marketplace',
    },
    imagePrompt: null,
    reason: 'A campus gig matching photography, one of your listed skills.',
    removalReason: null,
  },
  {
    id: 'pst-6',
    kind: 'listing',
    author: authors.tunde,
    body: 'Selling my MacBook Air M2, 16GB, bought last September. Battery health 94%. Selling because I have moved to a desktop for CAD work.',
    at: '2026-08-11T16:40:00Z',
    audience: 'campus',
    audienceLabel: 'FUTA Akure campus',
    spaceId: null,
    campusId: MY_CAMPUS,
    state: 'published',
    reactions: 31,
    myReaction: false,
    saved: false,
    comments: [],
    commentsLocked: false,
    poll: null,
    answers: [],
    resolved: false,
    attached: {
      kind: 'listing',
      label: 'MacBook Air M2 — ₦850,000',
      detail: 'Opens the marketplace order flow, with escrow',
      to: '/marketplace',
    },
    imagePrompt: null,
    reason: 'Marketplace activity on your campus.',
    removalReason: null,
  },
  {
    id: 'pst-7',
    kind: 'study_group',
    author: authors.tunde,
    body: 'Engineering Mathematics study group meets 3:30 today, second floor of the library. We are working through Laplace transforms — past questions attached in the group files.',
    at: '2026-08-14T05:30:00Z',
    audience: 'group',
    audienceLabel: 'MEE 301 study group members',
    spaceId: 'grp-1',
    campusId: MY_CAMPUS,
    state: 'published',
    reactions: 17,
    myReaction: true,
    saved: false,
    comments: [],
    commentsLocked: false,
    poll: null,
    answers: [],
    resolved: false,
    attached: {
      kind: 'course',
      label: 'MEE 301 — Engineering Mathematics',
      detail: 'Materials and recorded lectures in Learning',
      to: '/academics',
    },
    imagePrompt: null,
    reason: 'From a study group you belong to.',
    removalReason: null,
  },
  {
    id: 'pst-8',
    kind: 'text',
    author: authors.chidera,
    body: 'This post was removed.',
    at: '2026-08-10T11:00:00Z',
    audience: 'campus',
    audienceLabel: 'FUTA Akure campus',
    spaceId: null,
    campusId: MY_CAMPUS,
    state: 'removed',
    reactions: 0,
    myReaction: false,
    saved: false,
    comments: [],
    commentsLocked: true,
    poll: null,
    answers: [],
    resolved: false,
    attached: null,
    imagePrompt: null,
    reason: 'Kept in place so the removal is visible rather than silent.',
    removalReason:
      'Removed by campus moderation for repeated promotional content. The author was notified and can appeal.',
  },
]

export const communities: SocialCommunity[] = [
  {
    id: 'com-eng',
    name: 'FUTA Engineering',
    category: 'Academic',
    description:
      'Coursework, departmental notices, past questions and lab coordination across the School of Engineering.',
    members: 2140,
    scope: 'Faculty',
    campusId: MY_CAMPUS,
    joined: true,
    following: true,
    moderators: ['@esa.futa', '@ifeoma.e'],
    activity: 'Very active',
    official: true,
  },
  {
    id: 'com-mech',
    name: 'Mechanical Engineering',
    category: 'Academic',
    description: 'Departmental community for Mechanical Engineering students across all levels.',
    members: 612,
    scope: 'Department',
    campusId: MY_CAMPUS,
    joined: true,
    following: false,
    moderators: ['@ifeoma.e'],
    activity: 'Active',
    official: true,
  },
  {
    id: 'com-entre',
    name: 'Akure Founders',
    category: 'Career',
    description:
      'Students building something. Weekly build-in-public threads, funding calls and introductions.',
    members: 318,
    scope: 'Campus',
    campusId: MY_CAMPUS,
    joined: false,
    following: false,
    moderators: ['@tunde.b'],
    activity: 'Active',
    official: false,
  },
  {
    id: 'com-photo',
    name: 'FUTA Photography',
    category: 'Social',
    description: 'Photo walks, gear swaps and paid shoot referrals around Akure.',
    members: 244,
    scope: 'Campus',
    campusId: MY_CAMPUS,
    joined: false,
    following: true,
    moderators: ['@chidera.o'],
    activity: 'Active',
    official: false,
  },
  {
    id: 'com-cedar',
    name: 'Cedarwood Residents',
    category: 'Campus',
    description:
      'Residents of Cedarwood, Oba Ile. Shared costs raised here appear as splits in Money.',
    members: 86,
    scope: 'Residence',
    campusId: MY_CAMPUS,
    joined: true,
    following: false,
    moderators: ['@cedarwood'],
    activity: 'Quiet',
    official: false,
  },
  {
    id: 'com-robotics',
    name: 'Robotics & Automation',
    category: 'Interest',
    description: 'Build logs, competition prep and component sourcing.',
    members: 189,
    scope: 'Campuzon',
    campusId: 'all',
    joined: false,
    following: false,
    moderators: ['@campuzon.community'],
    activity: 'Quiet',
    official: false,
  },
]

export const clubs: Club[] = [
  {
    id: 'clb-esa',
    name: 'Engineering Students Association',
    category: 'Faculty association',
    description:
      'The recognised student association for the School of Engineering. Runs the career series, the departmental socials and the interfaculty sports entry.',
    campusId: MY_CAMPUS,
    institution: 'Federal University of Technology, Akure',
    members: 1980,
    verified: true,
    verificationNote:
      'Verified against the institution’s register of recognised student associations on 12 February 2026.',
    officers: [
      { name: 'Ifeoma Eze', role: 'President', handle: 'ifeoma.e' },
      { name: 'Tunde Bakare', role: 'General Secretary', handle: 'tunde.b' },
    ],
    myRole: 'Member',
    membershipRequests: 14,
    eventIds: ['evt-career-talk'],
    achievements: ['Interfaculty Football runners-up 2025', 'Best association event 2025'],
    joined: true,
  },
  {
    id: 'clb-photo',
    name: 'Akure Lens Collective',
    category: 'Creative club',
    description: 'Student photographers. Monthly photo walks and a shared equipment pool.',
    campusId: MY_CAMPUS,
    institution: 'Federal University of Technology, Akure',
    members: 132,
    verified: false,
    verificationNote:
      'Not verified by Campuzon. This club has not been confirmed against the institution’s register.',
    officers: [{ name: 'Chidera Okonkwo', role: 'Coordinator', handle: 'chidera.o' }],
    myRole: null,
    membershipRequests: 0,
    eventIds: [],
    achievements: [],
    joined: false,
  },
  {
    id: 'clb-debate',
    name: 'FUTA Debate Society',
    category: 'Academic club',
    description: 'Weekly parliamentary debate and inter-university competition entry.',
    campusId: MY_CAMPUS,
    institution: 'Federal University of Technology, Akure',
    members: 74,
    verified: true,
    verificationNote: 'Verified against the institution’s register on 3 March 2026.',
    officers: [{ name: 'Adaeze Nwosu', role: 'President', handle: 'adaeze.n' }],
    myRole: null,
    membershipRequests: 0,
    eventIds: [],
    achievements: ['National quarter-finalists 2025'],
    joined: false,
  },
]

export const stories: Story[] = [
  {
    id: 'sty-1',
    author: authors.esa,
    caption: 'Auditorium filling up for the career talk.',
    postedAt: '2026-08-14T09:10:00Z',
    expiresAt: '2026-08-15T09:10:00Z',
    audience: 'campus',
    views: 412,
    seen: false,
    imagePrompt: 'lecture auditorium filling with students',
  },
  {
    id: 'sty-2',
    author: authors.chidera,
    caption: 'Photo walk at the north gate, 6am light.',
    postedAt: '2026-08-14T06:20:00Z',
    expiresAt: '2026-08-15T06:20:00Z',
    audience: 'connections',
    views: 38,
    seen: false,
    imagePrompt: 'early morning campus gate photography',
  },
  {
    id: 'sty-3',
    author: authors.tunde,
    caption: 'Study group, second floor. Two seats left.',
    postedAt: '2026-08-13T15:40:00Z',
    expiresAt: '2026-08-14T15:40:00Z',
    audience: 'connections',
    views: 21,
    seen: true,
    imagePrompt: 'library study table with notebooks',
  },
]

export const challenges: Challenge[] = [
  {
    id: 'chl-hack',
    name: 'Akure Innovation Hackathon',
    category: 'Hackathon',
    description:
      'Forty-eight hours, teams of up to four, building something for a local problem. Judged by three engineers and one founder.',
    campusId: MY_CAMPUS,
    state: 'open',
    team: true,
    participants: 96,
    opensAt: '2026-08-22T08:00:00Z',
    closesAt: '2026-08-24T18:00:00Z',
    organiser: 'Akure Founders',
    registered: false,
    submissionRequired: 'A working prototype and a two-page write-up.',
    recognition: 'Participation recorded on your campus profile. Judges publish written feedback.',
  },
  {
    id: 'chl-read',
    name: '30-Day Reading Challenge',
    category: 'Reading',
    description:
      'One chapter a day from the library catalogue. Progress is private unless you choose to share it.',
    campusId: MY_CAMPUS,
    state: 'in_progress',
    team: false,
    participants: 214,
    opensAt: '2026-08-01T00:00:00Z',
    closesAt: '2026-08-31T23:59:00Z',
    organiser: 'FUTA Library',
    registered: true,
    submissionRequired: 'Nothing to submit. You mark your own progress.',
    recognition: 'No leaderboard. Completion is recorded privately unless you publish it.',
  },
  {
    id: 'chl-football',
    name: 'Interfaculty Football',
    category: 'Sport',
    description: 'Faculty teams, group stage then knockout. Registration through your association.',
    campusId: MY_CAMPUS,
    state: 'judging',
    team: true,
    participants: 340,
    opensAt: '2026-07-10T00:00:00Z',
    closesAt: '2026-08-18T00:00:00Z',
    organiser: 'FUTA Sports Council',
    registered: false,
    submissionRequired: 'Team registration through a recognised association.',
    recognition: 'Results published by the Sports Council.',
  },
]

export const myAchievements: Achievement[] = [
  {
    id: 'ach-1',
    label: 'Event volunteer',
    detail: 'Volunteered at three campus events this session.',
    earnedFrom: 'Attendance records for ESA Career Series, Freshers Social, Innovation Week',
    earnedAt: '2026-07-02T00:00:00Z',
    visible: true,
  },
  {
    id: 'ach-2',
    label: 'Community contributor',
    detail: 'Eleven answers marked helpful in FUTA Engineering.',
    earnedFrom: 'Helpful marks from other students, excluding your own',
    earnedAt: '2026-06-18T00:00:00Z',
    visible: true,
  },
  {
    id: 'ach-3',
    label: 'Study group organiser',
    detail: 'Ran the MEE 301 study group for a full semester.',
    earnedFrom: 'Group ownership record and eleven recorded sessions',
    earnedAt: '2026-05-30T00:00:00Z',
    visible: false,
  },
]

export const myReputation: ReputationFacet[] = [
  {
    dimension: 'Marketplace',
    value: '4.8 ★ over 12 orders',
    basis: 'Buyer reviews on completed orders only.',
  },
  {
    dimension: 'Community',
    value: 'Active contributor',
    basis: 'Eleven answers marked helpful by other students.',
  },
  { dimension: 'Events', value: '12 participations', basis: 'Recorded attendance, not RSVPs.' },
  {
    dimension: 'Mentorship',
    value: 'No sessions yet',
    basis: 'Shown as no data rather than as a zero score.',
  },
  {
    dimension: 'Identity',
    value: 'Email and institution verified',
    basis: 'Per attribute. There is no blanket “verified user” claim.',
  },
]

export const trending: TrendingTopic[] = [
  {
    id: 'trd-1',
    label: 'Registration deadline',
    signal: 'Discussion volume across four departmental communities',
    participants: 186,
    to: '/academics',
  },
  {
    id: 'trd-2',
    label: 'Oba Ile rent increases',
    signal: 'Questions asked and housing searches on your campus',
    participants: 94,
    to: '/housing',
  },
  {
    id: 'trd-3',
    label: 'Innovation Hackathon',
    signal: 'Challenge registrations and community posts',
    participants: 96,
    to: '/social',
  },
  {
    id: 'trd-4',
    label: 'Laptop repair near campus',
    signal: 'Repeated questions with no resolved answer',
    participants: 41,
    to: '/marketplace',
  },
]

export const dailyItems: DailyItem[] = [
  { id: 'dly-1', label: 'Events today', count: 3, to: '/community', urgent: false },
  { id: 'dly-2', label: 'Unread messages', count: 2, to: '/community', urgent: false },
  { id: 'dly-3', label: 'Group discussions', count: 4, to: '/social', urgent: false },
  { id: 'dly-4', label: 'Roommate match', count: 1, to: '/housing/roommates', urgent: false },
  { id: 'dly-5', label: 'Marketplace updates', count: 2, to: '/marketplace', urgent: false },
  { id: 'dly-6', label: 'Application deadline', count: 1, to: '/discover', urgent: true },
  { id: 'dly-7', label: 'New opportunities', count: 3, to: '/opportunities', urgent: false },
]

export const happenings: HappeningItem[] = [
  {
    id: 'hap-1',
    time: '10:00',
    day: 'Today',
    title: 'ESA Career Talk and CV clinic',
    host: 'Engineering Students Association',
    place: 'Engineering Auditorium',
    attending: 'going',
    to: '/community',
    kind: 'Career session',
  },
  {
    id: 'hap-2',
    time: '13:00',
    day: 'Today',
    title: 'Interfaculty Football — semi-final',
    host: 'FUTA Sports Council',
    place: 'Main Sports Complex',
    attending: 'interested',
    to: '/social',
    kind: 'Sport',
  },
  {
    id: 'hap-3',
    time: '15:30',
    day: 'Today',
    title: 'Engineering Mathematics study group',
    host: 'MEE 301 study group',
    place: 'Library, second floor',
    attending: 'going',
    to: '/academics',
    kind: 'Study group',
  },
  {
    id: 'hap-4',
    time: '18:00',
    day: 'Today',
    title: 'Freshers Social',
    host: 'Students Union',
    place: 'Student Centre',
    attending: 'none',
    to: '/community',
    kind: 'Social',
  },
  {
    id: 'hap-5',
    time: 'Tue 11:00',
    day: 'This week',
    title: 'Robotics build session',
    host: 'Robotics & Automation',
    place: 'Workshop B',
    attending: 'none',
    to: '/social',
    kind: 'Club meeting',
  },
  {
    id: 'hap-6',
    time: 'Thu 16:00',
    day: 'This week',
    title: 'Photo walk — north gate',
    host: 'Akure Lens Collective',
    place: 'North gate',
    attending: 'interested',
    to: '/social',
    kind: 'Social',
  },
  {
    id: 'hap-7',
    time: '22 Aug',
    day: 'Upcoming',
    title: 'Akure Innovation Hackathon',
    host: 'Akure Founders',
    place: 'Innovation Hub',
    attending: 'none',
    to: '/social',
    kind: 'Competition',
  },
]

export const interestOptions = [
  'Technology',
  'Sports',
  'Music',
  'Entrepreneurship',
  'Research',
  'Gaming',
  'Fitness',
  'Photography',
  'Business',
  'Engineering',
  'Books',
  'Arts',
  'Volunteering',
] as const
